import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, UserPlus, Calendar, User, Check, Loader } from 'lucide-react';
import participantService from '../../services/participantService';
import { toast } from 'react-hot-toast';

const ParticipantActivation = () => {
  const { studyID, siteID, participantID } = useParams();
  const navigate = useNavigate();
  const [participant, setParticipant] = useState(null);
  const [displayParticipantId, setDisplayParticipantId] = useState('');
  const [confirmActivation, setConfirmActivation] = useState(false);
  const [reactivationDate, setReactivationDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchParticipant = async () => {
      try {
        const data = await participantService.getParticipant(participantID);
        console.log('Full participant data:', data);
        setParticipant(data);
        
        // Extract human-readable ID (try multiple possible locations in the API response)
        const humanReadableId = data?.participant_id || 
                               data?.id || 
                               data?.participant?.participant_id || 
                               '';
        
        // If we still don't have a human-readable ID, use the breadcrumb service
        if (!humanReadableId || humanReadableId === participantID) {
          // We'll fetch from the breadcrumb service in another function
          fetchHumanReadableIdFromBreadcrumbs();
        } else {
          setDisplayParticipantId(humanReadableId);
        }
      } catch (error) {
        console.error('Error fetching participant:', error);
        toast.error('Failed to fetch participant details.');
        // Still try the breadcrumb service as a fallback
        fetchHumanReadableIdFromBreadcrumbs();
      } finally {
        setLoading(false);
      }
    };
    
    const fetchHumanReadableIdFromBreadcrumbs = async () => {
      try {
        // Import dynamically to avoid circular dependencies
        const BreadcrumbService = (await import('../../services/breadcrumbService')).default;
        const breadcrumbData = await BreadcrumbService.getBreadcrumbData();
        
        if (breadcrumbData && breadcrumbData.participants && breadcrumbData.participants[participantID]) {
          setDisplayParticipantId(breadcrumbData.participants[participantID]);
        }
      } catch (err) {
        console.error('Error fetching from breadcrumb service:', err);
      }
    };
    
    fetchParticipant();
  }, [participantID]);

  const handleRedirect = () => {
    const redirectSiteID = siteID || (participant?.site?.id);
    if (redirectSiteID) {
      navigate(`/studies/${studyID}/sites/${redirectSiteID}`);
    } else {
      console.warn('Site ID not found, redirecting to study page');
      navigate(`/studies/${studyID}`);
    }
  };

  const isNotLaterThanToday = (dateStr) => {
    let selectedDate = new Date(dateStr + " 00:00:01");
    let today = new Date();
    today.setHours(23, 59, 59);
    return selectedDate <= today;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmActivation) {
      toast.error('Please confirm that you want to activate this participant.');
      return;
    }
    if (!reactivationDate) {
      toast.error('Please select a reactivation date.');
      return;
    }
    if (!isNotLaterThanToday(reactivationDate)) {
      toast.error('The selected date must be today or in the past.');
      return;
    }
    try {
      await participantService.activateParticipant(participantID, reactivationDate);
      toast.success('Participant activated successfully');
      handleRedirect();
    } catch (error) {
      console.error('Error activating participant:', error);
      toast.error('Failed to activate participant. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden dark:shadow-gray-900/20">
            <div className="px-6 py-4 bg-primary-700">
              <h2 className="text-2xl font-semibold flex items-center text-white">
                <UserPlus className="w-6 h-6 mr-2" />
                Participant Activation
              </h2>
            </div>
            <div className="p-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <p className="text-gray-700 mb-4">
                  You are about to reactivate a participant. This action will allow the participant to resume all activities in the portal and appear on eCOA devices.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-blue-400 mr-3" />
                    <p className="text-sm text-blue-700">
                      Please ensure that reactivating this participant complies with the study protocol and has been approved by the appropriate personnel.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="participant-id">
                      Participant ID
                    </label>
                    <div className="relative">
                      <input
                        id="participant-id"
                        type="text"
                        value={displayParticipantId || participantID}
                        readOnly
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-gray-100 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="reactivation-date">
                      Reactivation Date
                    </label>
                    <div className="relative">
                      <input
                        id="reactivation-date"
                        type="date"
                        value={reactivationDate}
                        onChange={(e) => setReactivationDate(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white dark:bg-gray-800"
                        style={{ colorScheme: 'light' }}
                      />
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={confirmActivation}
                          onChange={(e) => setConfirmActivation(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 border-2 rounded-sm transition-colors duration-200 ease-in-out ${confirmActivation ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`}>
                          <Check className={`w-4 h-4 text-white transition-opacity duration-200 ease-in-out ${confirmActivation ? 'opacity-100' : 'opacity-0'}`} />
                        </div>
                      </div>
                      <span className="ml-2 text-sm text-gray-700">
                        YES, I want to activate this participant.
                      </span>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleRedirect}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                    >
                      Cancel
                    </button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                    >
                      <UserPlus className="w-5 h-5 mr-2" />
                      Activate
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ParticipantActivation;